/**
 * Excel Authorization Page
 *
 * OAuth-style consent page for Excel plugin authentication.
 * Opens in a browser from the Excel plugin via device code flow.
 *
 * Flow:
 * 1. Excel plugin generates a device code and opens this page with ?code=xxx
 * 2. User logs in with Clerk (if not already)
 * 3. User clicks "Authorize"
 * 4. This page calls /api/private/excel/authorize to link a token to the code
 * 5. Excel plugin polls and retrieves the token
 * 6. User can close the browser window
 */

import { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2, AlertCircle, X } from 'lucide-react';
import {
  Button,
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import { z } from 'zod';

import logoDark from '../../../assets/logo-dark.png';
import logoLight from '../../../assets/logo-light.png';

import { useRpc } from '@/lib/rpc';
import { useTheme } from '@/hooks/useTheme';
import { queryKeys } from '@/lib/queryKeys';

/** Workspace option for selector */
interface WorkspaceOption {
  id: string;
  name: string;
}

const searchParamsSchema = z.object({
  code: z.string().optional(),
});

/** Schema for API error responses */
const apiErrorSchema = z.object({
  error: z.string().optional(),
  message: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/excel/authorize')({
  validateSearch: searchParamsSchema,
  component: ExcelAuthorizePage,
});

/** Permission items shown to user */
const PERMISSIONS = [
  'Access your tables and data',
  'Access linked tables and queries',
  'Read your workspace information',
  'Sync data to your Excel workbooks',
];

type AuthState = 'ready' | 'authorizing' | 'success' | 'error' | 'declined';

function ExcelAuthorizePage() {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { code } = Route.useSearch();
  const rpc = useRpc();
  const { resolvedTheme } = useTheme();

  const [authState, setAuthState] = useState<AuthState>('ready');
  const [error, setError] = useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);

  // Theme-aware logo - default to light logo while theme resolves to prevent flicker
  const logo = resolvedTheme === 'dark' ? logoDark : logoLight;
  const isThemeReady = resolvedTheme !== undefined;

  // Query is only enabled when user is loaded and authenticated
  const queryEnabled = isUserLoaded && !!user;

  // Fetch workspaces using TanStack Query
  const {
    data: workspaces = [],
    isLoading: loadingWorkspaces,
    isPending: workspacesPending,
    error: workspacesError,
  } = useQuery({
    queryKey: queryKeys.workspaces.all,
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.all.$get();
      if (!res.ok) {
        throw new Error('Failed to fetch workspaces');
      }
      const data = await res.json();
      return (data.workspaces ?? []) as WorkspaceOption[];
    },
    enabled: queryEnabled,
  });

  // Consider loading if query is pending (no data yet) and enabled
  const isWorkspacesLoading = queryEnabled && (loadingWorkspaces || workspacesPending);

  // Auto-select first workspace when workspaces load
  useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspaceId) {
      setSelectedWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, selectedWorkspaceId]);

  // Set error if workspace fetch fails
  useEffect(() => {
    if (workspacesError) {
      setError('Failed to load workspaces');
    }
  }, [workspacesError]);

  // Show loading state while user or theme is loading to prevent flickering
  if (!isUserLoaded || !isThemeReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/50">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No code provided - show error
  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/50 p-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
            <h1 className="mt-4 text-lg font-semibold">Missing Authorization Code</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This page should be opened from the Loada Excel plugin. Please open Excel and click
              &quot;Sign in with Loada&quot; in the plugin panel.
            </p>
            <Button asChild variant="outline" className="mt-6">
              <a href="https://loada.io/docs/excel-integration" target="_blank" rel="noopener">
                View Setup Guide
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle authorize click
  const handleAuthorize = async () => {
    if (!selectedWorkspaceId) {
      setError('Please select a workspace');
      return;
    }

    setAuthState('authorizing');
    setError(null);

    try {
      const res = await rpc.api.private.excel.authorize.$post({
        json: { code, workspaceId: selectedWorkspaceId },
      });

      if (!res.ok) {
        const rawData = await res.json().catch(() => ({}));
        const parseResult = apiErrorSchema.safeParse(rawData);
        const errorMessage = parseResult.success
          ? parseResult.data.error || parseResult.data.message || 'Failed to authorize'
          : 'Failed to authorize';
        throw new Error(errorMessage);
      }

      setAuthState('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to authorize');
      setAuthState('error');
    }
  };

  // Handle decline click
  const handleDecline = () => {
    setAuthState('declined');
  };

  // Success state
  if (authState === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/50 p-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-6 w-6 text-primary" />
            </div>
            <h1 className="mt-4 text-lg font-semibold">Connected!</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Authorization successful. You can close this window and return to Excel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Declined state
  if (authState === 'declined') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/50 p-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <X className="h-6 w-6 text-muted-foreground" />
            </div>
            <h1 className="mt-4 text-lg font-semibold">Authorization Declined</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You can close this window. The Excel plugin will not be connected.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main consent UI
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 p-6">
      <div className="w-full max-w-md space-y-4">
        <Card>
          <CardHeader className="items-center text-center">
            {/* Logo */}
            <img src={logo} alt="Loada" className="mx-auto h-10 w-10" />
            <CardTitle className="text-lg">Connect to Loada</CardTitle>
            <CardDescription>
              Loada for Excel is requesting access to your account
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Workspace Selector */}
            <div className="space-y-2">
              {isWorkspacesLoading ? (
                <div className="flex h-10 items-center gap-2 rounded-md border px-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading workspaces...</span>
                </div>
              ) : workspaces.length === 0 ? (
                <div className="flex h-10 items-center rounded-md border px-3">
                  <span className="text-sm text-muted-foreground">No workspaces found</span>
                </div>
              ) : (
                <Select value={selectedWorkspaceId ?? ''} onValueChange={setSelectedWorkspaceId}>
                  <SelectTrigger id="workspace-select" className="w-full">
                    <SelectValue placeholder="Select a workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((ws) => (
                      <SelectItem key={ws.id} value={ws.id}>
                        {ws.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                The Excel Add-in will only access data from this workspace
              </p>
            </div>

            {/* Permissions */}
            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="mb-3 text-xs font-medium text-muted-foreground">
                This will allow the Excel Add-in to:
              </p>
              <ul className="space-y-2">
                {PERMISSIONS.map((permission) => (
                  <li key={permission} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm">{permission}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Error */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="break-words">{error}</AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="space-y-2">
              <Button
                onClick={handleAuthorize}
                disabled={authState === 'authorizing' || isWorkspacesLoading || !selectedWorkspaceId}
                className="w-full"
              >
                {authState === 'authorizing' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Authorizing...
                  </>
                ) : (
                  'Authorize'
                )}
              </Button>
              <Button
                onClick={handleDecline}
                variant="ghost"
                disabled={authState === 'authorizing'}
                className="w-full"
              >
                Decline
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        {user && (
          <p className="text-center text-xs text-muted-foreground">
            Signed in as {user.primaryEmailAddress?.emailAddress}
          </p>
        )}
      </div>
    </div>
  );
}
